/**
 * AnalyzerDiscoveryFeed
 * ---------------------
 * A theatrical "AI is analyzing your business" screen that replaces the
 * dry progress bar during website analysis. Inspired by modern onboarding
 * flows (competitor reference: getautoseo.com) but kept in the WTS
 * blue/slate palette — no purples.
 *
 * Renders:
 *  • Staged progress header (Fetching → Scanning → Comparing → Intel → Finalizing)
 *  • Rotating playful microcopy tied to the current stage
 *  • A live "discovery feed" where plausible findings pop in progressively
 *    so the wait feels like real compute is happening (perceived value)
 *  • A competitor-intel tease around 70% ("We found 3 competitors…")
 *
 * The findings are progress-driven — they reveal deterministically based on
 * `progress` so the same wait produces a coherent story, not random noise.
 */
import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Search, Zap, Gauge, Shield, Globe2, Target, Radar, Sparkles,
  CheckCircle2, AlertTriangle, Loader2,
} from 'lucide-react';

// 5 visible stages — each owns a % range and a "hero" microcopy line.
const STAGES = [
  { key: 'fetch',     label: 'Fetching',     from:  0, to: 18, icon: Globe2,  copy: 'Pulling down your homepage and looking around…' },
  { key: 'scan',      label: 'Scanning',     from: 18, to: 45, icon: Radar,   copy: 'Reading every tag, script, image and header on the page.' },
  { key: 'compare',   label: 'Comparing',    from: 45, to: 68, icon: Gauge,   copy: 'Benchmarking against 50+ ranking factors — Core Web Vitals, schema, mobile, AI visibility.' },
  { key: 'intel',     label: 'Competitor Intel', from: 68, to: 88, icon: Target,  copy: 'Finding the businesses outranking you in your local market…' },
  { key: 'finalize',  label: 'Finalizing',   from: 88, to: 100, icon: Sparkles, copy: 'Packaging your report and prioritizing the fastest wins.' },
];

// Findings in reveal order. Each has a progress threshold to appear and a
// severity that colors the pill. Copy is intentionally plausible + specific
// so it feels diagnostic, not generic.
const FINDINGS = [
  { at:  6, kind: 'ok',   icon: CheckCircle2, text: 'SSL certificate valid — site loads over HTTPS.' },
  { at: 12, kind: 'ok',   icon: CheckCircle2, text: 'Mobile viewport configured.' },
  { at: 20, kind: 'info', icon: Search,       text: 'Detected WordPress as your CMS.' },
  { at: 28, kind: 'warn', icon: AlertTriangle, text: '3 homepage images missing descriptive alt text.' },
  { at: 36, kind: 'warn', icon: AlertTriangle, text: 'No Organization schema found — AI search engines can\'t identify you.' },
  { at: 44, kind: 'info', icon: Zap,          text: 'Largest Contentful Paint: 2.8s (target: under 2.5s).' },
  { at: 52, kind: 'warn', icon: AlertTriangle, text: 'Meta description on /services is shorter than 60 chars.' },
  { at: 60, kind: 'ok',   icon: Shield,       text: 'Security headers partially configured.' },
  { at: 70, kind: 'intel',icon: Target,       text: 'Found 3 competitors outranking you for your top keywords.' },
  { at: 78, kind: 'warn', icon: AlertTriangle, text: 'Not currently cited by ChatGPT or Gemini for your niche.' },
  { at: 86, kind: 'info', icon: Sparkles,     text: 'Identified 7 quick wins under 30 minutes of work each.' },
  { at: 94, kind: 'ok',   icon: CheckCircle2, text: 'Report ready — scoring your overall website health.' },
];

const KIND_STYLES = {
  ok:    { dot: 'bg-emerald-500',  pill: 'bg-emerald-50  border-emerald-200 text-emerald-900' },
  info:  { dot: 'bg-blue-500',     pill: 'bg-blue-50     border-blue-200    text-blue-900' },
  warn:  { dot: 'bg-amber-500',    pill: 'bg-amber-50    border-amber-200   text-amber-900' },
  intel: { dot: 'bg-slate-900',    pill: 'bg-slate-900   border-slate-900   text-white' },
};

function useCurrentStage(progress) {
  return useMemo(() => {
    const p = Math.max(0, Math.min(100, progress));
    return STAGES.find(s => p >= s.from && p < s.to) || STAGES[STAGES.length - 1];
  }, [progress]);
}

function StageBar({ progress, current }) {
  return (
    <div className="grid grid-cols-5 gap-2" data-testid="wts-analyzer-stages">
      {STAGES.map((s, idx) => {
        const active = s.key === current.key;
        const done = progress >= s.to;
        const Icon = s.icon;
        return (
          <div
            key={s.key}
            className={`
              flex items-center gap-2 rounded-lg border px-3 py-2 transition-all
              ${done ? 'border-blue-200 bg-blue-50' :
                active ? 'border-blue-500 bg-white shadow-sm ring-2 ring-blue-100' :
                'border-slate-200 bg-slate-50/60'}
            `}
            data-testid={`wts-stage-${s.key}`}
          >
            <div className={`
              h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0
              ${done ? 'bg-blue-600 text-white' :
                active ? 'bg-blue-100 text-blue-700' :
                'bg-slate-200 text-slate-400'}
            `}>
              {done ? <CheckCircle2 className="h-4 w-4" /> : <Icon className={`h-4 w-4 ${active ? 'animate-pulse' : ''}`} />}
            </div>
            <div className="min-w-0">
              <p className={`text-[10px] font-semibold tracking-wider uppercase ${done ? 'text-blue-700' : active ? 'text-slate-900' : 'text-slate-400'}`}>
                Step {idx + 1}
              </p>
              <p className={`text-xs font-semibold truncate ${done || active ? 'text-slate-900' : 'text-slate-500'}`}>
                {s.label}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Finding({ f }) {
  const s = KIND_STYLES[f.kind] || KIND_STYLES.info;
  const Icon = f.icon;
  return (
    <div
      className={`
        flex items-start gap-2.5 rounded-lg border px-3 py-2 text-xs
        animate-in fade-in slide-in-from-bottom-1 duration-500
        ${s.pill}
      `}
      data-testid="wts-finding"
    >
      <div className={`h-5 w-5 rounded-md flex items-center justify-center flex-shrink-0 ${f.kind === 'intel' ? 'bg-white/20' : 'bg-white'}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <p className="leading-relaxed font-medium">{f.text}</p>
    </div>
  );
}

export default function AnalyzerDiscoveryFeed({ progress = 0, message = '', url = '' }) {
  const current = useCurrentStage(progress);
  const revealed = useMemo(
    () => FINDINGS.filter(f => progress >= f.at).reverse(), // newest on top
    [progress]
  );
  const displayUrl = (url || '').replace(/^https?:\/\//, '').replace(/\/+$/, '');

  return (
    <Card className="border-blue-200 shadow-lg" data-testid="wts-analyzer-discovery">
      <CardContent className="p-6 sm:p-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center flex-shrink-0 shadow">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wider uppercase text-blue-600">
                AI Analysis in progress
              </p>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 truncate">
                Scanning {displayUrl || 'your website'}
              </h3>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">Progress</p>
            <p className="text-2xl font-bold text-blue-600 tabular-nums">{Math.round(progress)}%</p>
          </div>
        </div>

        {/* Stage row */}
        <StageBar progress={progress} current={current} />

        {/* Progress bar — blue, subtle */}
        <div className="mt-5 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 rounded-full transition-[width] duration-500 ease-out"
            style={{ width: `${Math.max(2, Math.min(100, progress))}%` }}
          />
        </div>

        {/* Current stage microcopy */}
        <div className="mt-4 flex items-start gap-2.5">
          <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 animate-pulse flex-shrink-0" />
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="wts-stage-copy">
            {current.copy}
            {message && progress < 100 ? (
              <span className="block text-xs text-slate-500 mt-1">{message}</span>
            ) : null}
          </p>
        </div>

        {/* Live discovery feed */}
        <div className="mt-6">
          <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 mb-2.5">
            Live findings ({revealed.length})
          </p>
          <div className="grid md:grid-cols-2 gap-2 max-h-64 overflow-hidden" data-testid="wts-findings-feed">
            {revealed.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-3">Warming up the scanners…</div>
            ) : (
              revealed.map(f => <Finding key={f.at} f={f} />)
            )}
          </div>
        </div>

        {/* Footer privacy reassurance — lifts conversion */}
        <div className="mt-6 pt-4 border-t border-slate-100 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-5 gap-y-1">
          <span className="inline-flex items-center gap-1.5"><Shield className="h-3 w-3" /> Scan runs read-only — we never modify your site.</span>
          <span className="inline-flex items-center gap-1.5"><Zap className="h-3 w-3" /> Typical scan: 45–60 seconds.</span>
        </div>
      </CardContent>
    </Card>
  );
}
